import { Injectable } from '@angular/core';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { Dao } from '@app/dao/dao';
import { DataLoaderAuthentified } from '@app/dao/data-loader-authentified';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { catchError, map, Observable, of } from 'rxjs';

export enum CustomerActivity {
  GP = 'GP',
  PROSPECTION = 'PROSPECTION',
  CREDIT_IMMO_PARTICULIER = 'CREDIT_IMMO_PARTICULIER',
  ASSURCOPRO = 'ASSURCOPRO',
  ASSURGERANCE = 'ASSURGERANCE',
  PROCARD = 'PROCARD',
  JBL_MRI = 'JBL_MRI',
  JBL_GLI = 'JBL_GLI',
}

export const CUSTOMER_ACTIVITY_LABELS: Record<CustomerActivity, string> = {
  [CustomerActivity.GP]: 'Gestion de patrimoine',
  [CustomerActivity.PROSPECTION]: 'Prospection',
  [CustomerActivity.CREDIT_IMMO_PARTICULIER]: 'Crédit immobilier particulier',
  [CustomerActivity.ASSURCOPRO]: 'Assurance copropriété',
  [CustomerActivity.ASSURGERANCE]: 'Assurance Gérance',
  [CustomerActivity.PROCARD]: 'Registre des cartes pro',
  [CustomerActivity.JBL_MRI]: 'JBL MRI',
  [CustomerActivity.JBL_GLI]: 'JBL GLI',
};

export enum BuFeature {
  SIMULATION_DOWNLOAD = 'SIMULATION_DOWNLOAD',
  SUBSCRIPTION = 'SUBSCRIPTION',
  WEALTH_ANALYSIS = 'WEALTH_ANALYSIS',
}

export const BU_FEATURE_ACTIVITIES: Record<BuFeature, CustomerActivity[]> = {
  [BuFeature.SIMULATION_DOWNLOAD]: [CustomerActivity.GP],
  [BuFeature.SUBSCRIPTION]: [CustomerActivity.GP],
  [BuFeature.WEALTH_ANALYSIS]: [CustomerActivity.GP],
};

export const NO_CUSTOMER_ACTIVITY_LABEL = 'Autre';

export type CustomerActivityLabels = Map<string, string>;

export function resolveCustomerActivityLabel(
  activity: CustomerActivity | null | undefined,
  serverLabel?: string,
  referenceLabels?: CustomerActivityLabels | null,
): string {
  return (
    serverLabel ||
    (activity && referenceLabels?.get(activity)) ||
    (activity && CUSTOMER_ACTIVITY_LABELS[activity]) ||
    activity ||
    NO_CUSTOMER_ACTIVITY_LABEL
  );
}

export interface CustomerActivityConsultantDto {
  memberId: number;
  firstName: string;
  lastName: string;
  poste: string | null;
  pictureUrl: string | null;
  calendlyUrl?: string | null;
}

export interface CustomerActivityContactsDto {
  customerId: number;
  activity: CustomerActivity;
  activityLabel: string;
  mainConsultant: CustomerActivityConsultantDto;
}

export interface ConversationAccessDto {
  activity: CustomerActivity;
  activityLabel: string | null;
  canRead: boolean;
  canWrite: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CustomerActivityDao extends Dao {
  private activityLabels: DataLoaderAuthentified<CustomerActivityLabels> =
    new DataLoaderAuthentified<CustomerActivityLabels>(
      this.getAuthentified(),
      () =>
        this.httpApi
          .getGeneric<
            { id: string; value: string }[]
          >('/ref/organizations/customers/activities')
          .pipe(
            map(
              references =>
                new Map(
                  (references ?? [])
                    .filter(reference => !!reference?.id)
                    .map(reference => [reference.id, reference.value]),
                ),
            ),
          ),
    );

  private myActivityContacts: DataLoaderAuthentified<
    CustomerActivityContactsDto[]
  > = new DataLoaderAuthentified<CustomerActivityContactsDto[]>(
    this.getAuthentified(),
    user => this.getActivityContacts(user.accountId),
  );

  constructor(
    httpApi: HttpApiService,
    authentificationService: AuthentificationService,
  ) {
    super(httpApi, authentificationService);
  }

  getActivityLabels(): Observable<CustomerActivityLabels> {
    return this.activityLabels.get();
  }

  getActivityContacts(
    accountId: number,
  ): Observable<CustomerActivityContactsDto[]> {
    return this.httpApi
      .getGeneric<
        CustomerActivityContactsDto[]
      >(`/accounts/${accountId}/customers/activity-contacts`)
      .pipe(map(contacts => contacts ?? []));
  }

  getMyActivityContacts(): Observable<CustomerActivityContactsDto[]> {
    return this.myActivityContacts.get();
  }

  canUse$(feature: BuFeature): Observable<boolean> {
    const requiredActivities = BU_FEATURE_ACTIVITIES[feature];
    return this.getMyActivityContacts().pipe(
      map(contacts =>
        contacts.some(contact => requiredActivities.includes(contact.activity)),
      ),
      catchError(() => of(false)),
    );
  }

  getConversationAccess(
    accountId: number,
  ): Observable<Map<CustomerActivity, ConversationAccessDto>> {
    return this.httpApi
      .getGeneric<
        ConversationAccessDto[]
      >(`/accounts/${accountId}/messages/access`)
      .pipe(
        map(
          entries =>
            new Map(
              (entries ?? [])
                .filter(entry => !!entry?.activity)
                .map(entry => [entry.activity, entry]),
            ),
        ),
      );
  }

  reset() {
    this.activityLabels?.reset();
    this.myActivityContacts?.reset();
  }

  onUserChange(_event: any): void {
    this.reset();
  }
}
